import { routeAfterAuthenticated, routeLogin } from '@/auth/constants/authSettings';
import useAuth from '@/auth/useAuth';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, Card, Container, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconHome, IconLogout, IconUsers } from '@tabler/icons-react';
import { t } from 'i18next';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import { useGroupCreate } from '@/api/gen-new/group/group';
import { useAuthMe } from '@/api/gen-new/auth/auth';
import { getSession, saveSession } from '@/auth/AuthSession';
import type { UserAuthJwtPayload } from '@/auth/AuthProvider';
import defaultNotifications from '@/components/functions/defaultNotifications';

const schema = yup.object({
	spaceName: yup.string().required('Space name is required').min(2, 'Space name must be at least 2 characters'),
});

type FormData = yup.InferType<typeof schema>;

export default function NoSpacePage() {
	const navigate = useNavigate();
	const { isAuthenticated, signOut } = useAuth();
	const [createError, setCreateError] = useState<string | null>(null);

	// Get current user information to set default space name
	const { data: user } = useAuthMe({
		query: {
			enabled: isAuthenticated(),
		},
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues: { spaceName: '' },
	});

	// Update form default value when user data loads
	useEffect(() => {
		if (user?.name) {
			const defaultSpaceName = `${user.name}'s Space`;
			reset({ spaceName: defaultSpaceName });
		}
	}, [user, reset]);

	const createGroupMutation = useGroupCreate({
		mutation: {
			onSuccess: async response => {
				defaultNotifications.showSuccess({
					message: t('spaces.newSpaceModal.notifications.success'),
				});

				// Decode the new JWT token to get the userGroupId
				const tokenData = jwtDecode<UserAuthJwtPayload>(response.accessToken);

				// Update session with new tokens that include the group ID
				const currentSession = getSession();
				if (currentSession) {
					// Update the session with new tokens and decoded data
					const updatedSession = {
						...currentSession,
						accessToken: response.accessToken,
						refreshToken: response.refreshToken,
						expiresIn: response.expiresIn,
						refreshTokenExpiresIn: response.refreshTokenExpiresIn,
						userGroupId: tokenData.userGroupId,
					};

					// Save updated session and navigate
					saveSession(updatedSession);
					navigate(routeAfterAuthenticated);
				} else {
					// Fallback if session is somehow missing
					window.location.reload();
				}
			},
			onError: (error: any) => {
				console.error('Failed to create space:', error);

				let errorMessage = 'Failed to create space. Please try again.';
				if (error?.message) {
					// Use the API error message if available
					errorMessage = error.message;
				}

				setCreateError(errorMessage);
				defaultNotifications.showError({
					message: errorMessage,
				});
			},
		},
	});

	useEffect(() => {
		if (!isAuthenticated()) {
			navigate('/login');
		}
	}, [isAuthenticated, navigate]);

	const onCreateSpace = async (data: FormData) => {
		setCreateError(null);
		createGroupMutation.mutate({
			data: { name: data.spaceName },
		});
	};

	const handleSignOut = () => {
		signOut();
		navigate(routeLogin);
	};

	return (
		<Container
			size="sm"
			style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
		>
			<Stack gap="lg" style={{ width: '100%', maxWidth: 500 }}>
				{/* Sign Out Button */}
				<Group justify="flex-end">
					<Button
						variant="subtle"
						color="gray"
						size="xs"
						leftSection={<IconLogout size={14} />}
						onClick={handleSignOut}
					>
						{t('noSpace.signOut')}
					</Button>
				</Group>
				{/* Header */}
				<Stack gap="sm" align="center">
					<Group justify="left" align="center">
						<IconUsers size={48} color="var(--mantine-color-blue-6)" />
						<Title order={2} ta="center">
							{t('noSpace.title')}
						</Title>
					</Group>
				</Stack>

				{/* Information Alert */}
				<Alert color="blue" variant="light">
					<Text size="sm">{t('noSpace.description')}</Text>
				</Alert>

				{/* Error Alert */}
				{createError && (
					<Alert color="red" variant="light">
						<Text size="sm">{createError}</Text>
					</Alert>
				)}

				{/* Create New Space Card */}
				<Card shadow="sm" padding="lg" radius="md" withBorder>
					<Stack gap="md">
						<Group gap="xs" justify="left" align="center">
							<IconHome size={20} />
							<Title order={4}>{t('noSpace.createSpace.title')}</Title>
						</Group>

						<Text size="sm" c="dimmed">
							{t('noSpace.createSpace.description')}
						</Text>

						<form onSubmit={handleSubmit(onCreateSpace)}>
							<Stack gap="md">
								<TextInput
									{...register('spaceName')}
									label={t('noSpace.createSpace.spaceName')}
									placeholder={t('noSpace.createSpace.spaceNamePlaceholder')}
									error={errors.spaceName?.message}
									disabled={createGroupMutation.isPending}
								/>

								<Button
									type="submit"
									variant="filled"
									loading={createGroupMutation.isPending}
									fullWidth
									leftSection={<IconHome size={16} />}
								>
									{t('noSpace.createSpace.button')}
								</Button>
							</Stack>
						</form>
					</Stack>
				</Card>
			</Stack>
		</Container>
	);
}
